import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeContextType {
  /** Resolved theme (always 'light' or 'dark') */
  theme: ResolvedTheme;
  /** User preference: light, dark, or system */
  themePreference: ThemePreference;
  setTheme: (preference: ThemePreference) => void;
  toggleTheme: () => void;
  colors: {
    background: string;
    surface: string;
    surfaceHover: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    heading: string;
    primary: string;
    primaryHover: string;
    border: string;
    borderLight: string;
    cardBg: string;
    inputBg: string;
    shadow: string;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const lightColors = {
  background: '#F7F8FA',
  surface: '#FFFFFF',
  surfaceHover: '#F0EEFF',
  text: '#464452',
  textSecondary: '#8B8A92',
  textTertiary: '#B4B3BA',
  heading: '#2D1F7B',
  primary: '#594BFF',
  primaryHover: '#8B7FFF',
  border: '#E2DFEA',
  borderLight: '#F0EDF7',
  cardBg: '#FFFFFF',
  inputBg: '#FFFFFF',
  shadow: 'rgba(89, 75, 255, 0.12)',
};

const darkColors = {
  background: '#0A0A0F',
  surface: '#16151D',
  surfaceHover: '#1F1E29',
  text: 'rgba(255, 255, 255, 0.95)',
  textSecondary: 'rgba(255, 255, 255, 0.70)',
  textTertiary: 'rgba(255, 255, 255, 0.45)',
  heading: 'rgba(255, 255, 255, 0.98)',
  primary: '#8B7FFF',
  primaryHover: '#A89FFF',
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.05)',
  cardBg: '#16151D',
  inputBg: '#0F0E16',
  shadow: 'rgba(0, 0, 0, 0.5)',
};

const STORAGE_KEY = 'petso-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemePreference | null;
    return saved === 'light' || saved === 'dark' || saved === 'system' ? saved : 'light';
  });

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setSystemTheme(getSystemTheme());
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const theme: ResolvedTheme = themePreference === 'system' ? systemTheme : themePreference;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, themePreference);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme, themePreference]);

  const setTheme = (preference: ThemePreference) => {
    setThemePreference(preference);
  };

  const toggleTheme = () => {
    setThemePreference((prev) => {
      const nextResolved = theme === 'light' ? 'dark' : 'light';
      if (prev === 'system') return nextResolved;
      return nextResolved;
    });
  };

  const colors = theme === 'light' ? lightColors : darkColors;

  const value = useMemo(
    () => ({ theme, themePreference, setTheme, toggleTheme, colors }),
    [theme, themePreference, colors]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}